/**
 * The table.
 *
 * Renders exactly what the last snapshot says and nothing it implies. Seats are
 * placed around the oval with the recipient's own seat rotated to the bottom —
 * every player sees themselves at six o'clock, the way every card room client
 * has worked since the first one, because reading your own position from a
 * fixed layout is work the player should not have to do.
 */

import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { TableSnapshot } from '@pf/protocol';

import { CATEGORY_LABEL, STREET_LABEL, bb } from '../fa.ts';
import type { Felt } from '../felt.ts';
import { winChances } from '../allin.ts';
import { strengthOf } from '../handStrength.ts';
import type { SeatAction } from '../state.ts';
import { Chip, type ChipKind } from '../brand.tsx';
import { Avatar } from './avatar.tsx';
import { CardBack, PlayingCard } from './cards.tsx';

/**
 * Where each seat sits on the oval, as a direction rather than a position.
 *
 * Only the angle is decided here: the distance from the middle is a CSS
 * variable, so a phone can pull the seats in without this file knowing how wide
 * the screen is. Layout that depends on the viewport belongs in the stylesheet,
 * which is the only place that can actually see it.
 */
function directions(count: number): { cx: number; cy: number }[] {
  const out: { cx: number; cy: number }[] = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.PI / 2 + (i * 2 * Math.PI) / count;
    out.push({ cx: Math.cos(angle), cy: Math.sin(angle) });
  }
  return out;
}

/** The custom properties every positioned thing on the felt is placed by. */
function at(direction: { cx: number; cy: number }): Record<string, string> {
  return { '--cx': String(direction.cx), '--cy': String(direction.cy) };
}

/** Live countdown for the acting seat, derived from the snapshot's deadline. */
function useCountdown(deadlineMs: number | null, totalMs: number): number {
  const [fraction, setFraction] = useState(1);
  useEffect(() => {
    if (deadlineMs === null) return;
    const expiresAt = Date.now() + deadlineMs;
    const tick = () => setFraction(Math.max(0, (expiresAt - Date.now()) / totalMs));
    tick();
    const timer = setInterval(tick, 500);
    return () => clearInterval(timer);
  }, [deadlineMs, totalMs]);
  return deadlineMs === null ? 1 : fraction;
}

/**
 * The verb, on the person who said it.
 *
 * English, like every other control a player reads at speed, and the amount
 * only where an amount is part of what happened — "CHECK 0" is noise.
 */
const ACTION_WORD: Record<SeatAction['kind'], string> = {
  fold: 'FOLD',
  check: 'CHECK',
  call: 'CALL',
  bet: 'BET',
  raise: 'RAISE',
  blind: 'BLIND',
};

/**
 * True for a moment after the hand you are holding becomes a better one.
 *
 * The turn arriving and making two pair is the news; two pair still being two
 * pair is not. Reset per hand, or the last hand's straight would make this
 * hand's pair look like a downgrade.
 */
function useImproved(category: string | null, handNumber: number): boolean {
  const seen = useRef<{ hand: number; rank: number }>({ hand: -1, rank: -1 });
  const [grew, setGrew] = useState(false);
  const rank = category === null ? -1 : CATEGORY_ORDER.indexOf(category);
  useEffect(() => {
    if (seen.current.hand !== handNumber) {
      seen.current = { hand: handNumber, rank };
      setGrew(false);
      return;
    }
    if (rank > seen.current.rank) {
      seen.current = { hand: handNumber, rank };
      setGrew(true);
      const timer = setTimeout(() => setGrew(false), 2_000);
      return () => clearTimeout(timer);
    }
    return;
  }, [rank, handNumber]);
  return grew;
}

/** Weakest to strongest, so "better than before" is a comparison and not a guess. */
const CATEGORY_ORDER = [
  'high-card',
  'pair',
  'two-pair',
  'trips',
  'straight',
  'flush',
  'full-house',
  'quads',
  'straight-flush',
];

/**
 * Hold a showdown back until the cards it explains have finished turning over.
 *
 * The server has no reason to stagger these: the hands are shown and the pot
 * is awarded in the same breath, because they happened in the same instant.
 * But a table that names the winner while the losing hand is still face down
 * has answered a question nobody has been allowed to ask yet — the whole point
 * of a showdown is the second where everyone can see and nobody has been told.
 *
 * Only when a result arrives while you are watching. Walking in on a table
 * that has already finished a hand should show you the finished hand.
 */
function useShowdownBeat(hasResult: boolean, waitMs: number): boolean {
  const [ready, setReady] = useState(hasResult);
  const had = useRef(hasResult);
  useEffect(() => {
    if (!hasResult) {
      had.current = false;
      setReady(false);
      return;
    }
    if (had.current) return;
    had.current = true;
    if (waitMs <= 0) {
      setReady(true);
      return;
    }
    setReady(false);
    const timer = setTimeout(() => setReady(true), waitMs);
    return () => clearTimeout(timer);
  }, [hasResult, waitMs]);
  return ready;
}

/**
 * The chips a bet is shown as.
 *
 * Measured in big blinds rather than in chips, because a table's chips are
 * whatever its blinds make them: two chips at 1/2 and two chips at 200/400
 * are the same bet and should look the same. The colours run in the order a
 * card room's do — the pale one is small, the dark one is real money, the red
 * one is more than most players have in front of them.
 *
 * Three discs at most. It is a stack, not a count.
 */
function stackFor(amount: number, bigBlind: number): ChipKind[] {
  const blinds = amount / Math.max(1, bigBlind);
  if (blinds >= 50) return ['red', 'red', 'midnight'];
  if (blinds >= 20) return ['midnight', 'midnight', 'ivory'];
  if (blinds >= 5) return ['ivory', 'ivory', 'brand'];
  if (blinds >= 2) return ['brand', 'ivory'];
  return ['brand'];
}

/** True for one render after `value` grows, so something can flash. */
function useGrew(value: number): boolean {
  const previous = useRef(value);
  const [grew, setGrew] = useState(false);
  useEffect(() => {
    if (value > previous.current) {
      setGrew(true);
      const timer = setTimeout(() => setGrew(false), 420);
      previous.current = value;
      return () => clearTimeout(timer);
    }
    previous.current = value;
    return undefined;
  }, [value]);
  return grew;
}

export function Table({
  snapshot,
  lastActions,
  felt,
  onSit,
}: {
  snapshot: TableSnapshot;
  lastActions: ReadonlyMap<number, SeatAction>;
  /** Which of the three tables this player is sitting at. */
  felt: Felt;
  onSit: (seat: number) => void;
}) {
  const seats = snapshot.seats;
  const positions = directions(snapshot.maxSeats);
  // Rotate so the viewer's seat lands on anchor 0 (six o'clock).
  const rotation = snapshot.yourSeat ?? 0;

  // The bar has to be measured against whichever clock is actually running, or
  // switching to the time bank would show a bar that jumps back to full and
  // then crawls — the opposite of the urgency it is there to convey.
  const clockTotal = snapshot.onTimeBank
    ? Math.max(1, seats.find((s) => s.seat === snapshot.actionOn)?.timeBankMs ?? 1)
    : 20_000;
  const clock = useCountdown(snapshot.actionDeadlineMs, clockTotal);
  const secondsLeft =
    snapshot.actionDeadlineMs === null ? null : Math.max(0, Math.ceil(clock * (clockTotal / 1_000)));
  const potGrew = useGrew(snapshot.potTotal);

  // The order a dealer would go in: one card to each player starting left of
  // the button, all the way round, then round again. It is the order every
  // card room deals in, and dealing two at a time instead — which is what
  // happens when nobody decides — is the difference between a hand starting
  // and a hand appearing.
  const dealOrder = new Map<number, number>();
  {
    const live = seats
      .filter((seat) => seat.cards !== null || seat.cardCount > 0)
      .map((seat) => seat.seat)
      .sort((a, b) => a - b);
    // No button between hands; then the order is simply seat order, which is
    // only ever used by an animation that is not running.
    const button = snapshot.buttonSeat ?? -1;
    const first = live.findIndex((seat) => seat > button);
    const clockwise = first === -1 ? live : [...live.slice(first), ...live.slice(0, first)];
    clockwise.forEach((seat, index) => dealOrder.set(seat, index));
  }
  const dealtIn = dealOrder.size;

  // Cards turn over one hand at a time, in seat order from the button, the way
  // a dealer tables them — not all at once, which reads as the result being
  // announced rather than the hands being shown.
  const revealOrder = new Map<number, number>();
  {
    let place = 0;
    for (const [seat] of [...dealOrder].sort((a, b) => a[1] - b[1])) {
      if (seats.find((entry) => entry.seat === seat)?.isYou === true) continue;
      revealOrder.set(seat, place);
      place += 1;
    }
  }

  // Long enough for the hands to turn over, capped: at a full table the last
  // card would otherwise land nearly two seconds after the first, and a pause
  // that long stops reading as suspense and starts reading as a stall.
  const turning = Math.min(revealOrder.size, 3) * 320 + 620;
  const showdownDone = useShowdownBeat(snapshot.result !== null, turning);
  const result = showdownDone ? snapshot.result : null;

  // While a result is on screen, the five cards that actually played stay lit
  // and everything else recedes. Naming the hand is not enough — showing which
  // cards made it is what turns 'you lost' into 'you lost to this'.
  const winner = result?.shown.find((shown) =>
    result.awards.some((a) => a.seat === shown.seat && a.amount > 0),
  );
  const playedCards = winner === undefined ? null : new Set(winner.best);
  const netBySeat = new Map((result?.net ?? []).map((n) => [n.seat, n.amount]));
  const winners = (result?.awards ?? [])
    .filter((a) => a.amount > 0)
    .map((a) => ({ seat: a.seat, name: seats.find((s) => s.seat === a.seat)?.name ?? '—' }))
    .filter((w, i, all) => all.findIndex((o) => o.seat === w.seat) === i);
  const wonTotal = (result?.awards ?? []).reduce((sum, a) => sum + a.amount, 0);
  // What each winner is owed, so the pot can be seen going to them rather than
  // simply appearing in their stack. Split pots get one flight each.
  const awardsBySeat = new Map<number, number>();
  for (const award of result?.awards ?? []) {
    if (award.amount > 0) awardsBySeat.set(award.seat, (awardsBySeat.get(award.seat) ?? 0) + award.amount);
  }
  const categoryBySeat = new Map((result?.shown ?? []).map((s) => [s.seat, s.category]));

  // What the viewer is holding, worked out here rather than asked for. Only
  // ever their own cards: nobody else's are in this browser to evaluate.
  // Once every live hand is face up, the decisions are over and only the
  // board is left. That is the one moment the numbers can go on screen without
  // helping anybody, and it is when every broadcast puts them there.
  const chances = useMemo(() => winChances(snapshot), [snapshot]);
  const chanceBySeat = new Map((chances ?? []).map((chance) => [chance.seat, chance]));

  const you = seats.find((seat) => seat.isYou);
  const yourHand =
    you?.cards == null || snapshot.result !== null
      ? null
      : strengthOf(you.cards, snapshot.board);
  const yourHandGrew = useImproved(yourHand, snapshot.handNumber);

  return (
    <div className="stage" data-felt={felt}>
      {/* The room's name on the cloth. An element rather than a third
          pseudo-element, because ::before is the watermark and ::after is the
          light in the room. */}
      <div className="felt-word" aria-hidden="true">
        IRAN POKER TOUR
      </div>
      <div className="centre">
        <span className="street-tag">{STREET_LABEL[snapshot.street]}</span>
        <div className="board">
          {snapshot.board.map((code, i) => (
            <PlayingCard
              key={code}
              code={code}
              size="board"
              flip
              // The flop stagger runs 0-1-2; the turn and river arrive alone
              // and should not sit waiting for a delay that means nothing.
              order={i < 3 ? i : 0}
              dimmed={playedCards !== null && !playedCards.has(code)}
            />
          ))}
        </div>
        {result !== null ? (
          <div className="result-banner">
            <span className="who">
              {winners.map((w) => w.name).join(' + ')} <b>+{bb(wonTotal, snapshot.bigBlind)}</b>
            </span>
            {winner !== undefined && (
              <span className="what">{CATEGORY_LABEL[winner.category]}</span>
            )}
          </div>
        ) : (
          <>
            <div className={`pot${snapshot.potTotal === 0 ? ' empty' : ''}${potGrew ? ' grew' : ''}`}>
              <span>Pot</span>
              <b className="num">{bb(snapshot.potTotal, snapshot.bigBlind)}</b>
            </div>
            {/* Side pots, once there are any.
                When somebody is all in for less than the bets around them, the
                money splits: they can only win what they could match, and the
                rest is contested by the players who still have chips behind.
                The engine has always built these correctly — the table simply
                never showed them, so a short stack all-in looked as though it
                were playing for everything. */}
            {snapshot.pots.length > 1 && (
              <div className="side-pots">
                {snapshot.pots.map((pot, index) => (
                  <div key={index} className="side-pot">
                    <span className="lat-ui">{index === 0 ? 'Main' : `Side ${index}`}</span>
                    <b className="num lat-ui">{bb(pot.amount, snapshot.bigBlind)}</b>
                    <span className="eligible lat-ui">{pot.eligible.length} in</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* The pot, going where it went.
          A number that changes in two places at once — the middle empties, a
          stack grows — makes a player work out what happened. Chips crossing
          the felt say it before anybody has to read anything, and at a
          showdown the eye is on the middle of the table anyway.
          Keyed on the hand, so the next pot is a new flight rather than a
          resumed one. */}
      {result !== null &&
        [...awardsBySeat].map(([seat, amount]) => {
          const anchor = positions[(seat - rotation + snapshot.maxSeats) % snapshot.maxSeats]!;
          return (
            <div
              key={`${snapshot.handNumber}-${seat}`}
              className="pot-fly"
              style={at(anchor) as React.CSSProperties}
              aria-hidden="true"
            >
              <span className="stack" aria-hidden="true">
                {stackFor(amount, snapshot.bigBlind).map((kind, index) => (
                  <Chip key={index} kind={kind} className="disc" />
                ))}
              </span>
              <b className="num">{bb(amount, snapshot.bigBlind)}</b>
            </div>
          );
        })}

      {seats.map((seat) => {
        const anchor = positions[(seat.seat - rotation + snapshot.maxSeats) % snapshot.maxSeats]!;
        const style = at(anchor) as React.CSSProperties;

        if (seat.status === 'empty') {
          if (snapshot.yourSeat !== null) return null;
          return (
            <button key={seat.seat} className="sit-btn" style={style} onClick={() => onSit(seat.seat)}>
              Sit Here
            </button>
          );
        }

        const acting = snapshot.actionOn === seat.seat;
        const classes = [
          'seat',
          seat.status === 'folded' ? 'folded' : '',
          acting ? 'acting' : '',
          (netBySeat.get(seat.seat) ?? 0) > 0 ? 'winner' : '',
          seat.isYou ? 'you' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div key={seat.seat} className={classes} style={style}>
            <div className="hole">
              {seat.cards !== null
                ? seat.cards.map((code, i) => (
                    <PlayingCard
                      key={code}
                      code={code}
                      // Your own cards are dealt to you; everybody else's are
                      // turned over. They are different events and they were
                      // being shown with the same animation — an opponent's
                      // hand flying in from the middle of the felt at showdown,
                      // as though the dealer had produced two more cards from
                      // somewhere at the end of the hand.
                      {...(seat.isYou
                        ? { deal: i * dealtIn + (dealOrder.get(seat.seat) ?? 0) }
                        : { reveal: revealOrder.get(seat.seat) ?? 0 })}
                      dimmed={playedCards !== null && seat.seat === winner?.seat && !playedCards.has(code)}
                    />
                  ))
                : Array.from({ length: seat.cardCount }, (_, i) => (
                    // Keyed on the hand as well as the position: a face-down
                    // card looks the same every hand, so without this React
                    // keeps the old element and the deal never animates again
                    // after the first one.
                    <CardBack
                      key={`${snapshot.handNumber}-${i}`}
                      deal={i * dealtIn + (dealOrder.get(seat.seat) ?? 0)}
                    />
                  ))}
            </div>
            {categoryBySeat.has(seat.seat) ? (
              <div className="hand-label">{CATEGORY_LABEL[categoryBySeat.get(seat.seat)!]}</div>
            ) : (
              seat.isYou &&
              yourHand !== null && (
                // Said only to the person holding the cards, and only once
                // there are five to make a hand out of. Naming two hole cards
                // "high card" pre-flop would read as a verdict on a hand
                // nobody has finished dealing.
                <div className={`hand-label yours${yourHandGrew ? ' grew' : ''}`}>
                  {CATEGORY_LABEL[yourHand] ?? yourHand}
                </div>
              )
            )}
            <div className="plate">
              {/* A face before a name. Six strangers are told apart by a
                  portrait far faster than by reading, and a drawn one costs
                  nothing to load. It hangs off the leading edge of the plate
                  rather than sitting inside it, so it can be big enough to
                  recognise from across the table without the plate growing to
                  hold it. */}
              <Avatar name={seat.name ?? '?'} />
              <div className="who">
                <div className="name">{seat.name}</div>
                <div className="chips num">{bb(seat.stack, snapshot.bigBlind)}</div>
              </div>
              {/* On the corner of their own plate, where a room card sits in
                  front of a real player — not floating in a row underneath
                  with the standing facts about them. */}
              {seat.status === 'all-in' && <span className="tag allin">ALL IN</span>}
              {seat.status === 'folded' && <span className="tag fold">FOLD</span>}
            </div>
            {/* Their own row under the plate, carrying the standing facts only.
                What somebody just did is on the plate itself. */}
            <div className="tags">
              {/* Said on the table, not only in the name. A player has a right
                  to know who they are playing against, and it should not take
                  reading a suffix to find out. */}
              {seat.isBot && <span className="tag bot lat-ui">BOT</span>}
              {seat.status === 'sitting-out' && <span className="tag">SITTING OUT</span>}
              {seat.status !== 'sitting-out' && seat.sitOutNextHand && (
                <span className="tag">SIT OUT NEXT</span>
              )}
            </div>
            {/* Keyed on the action's id so a second call on the same street
                replays the animation instead of sitting there looking stale. */}
            {(() => {
              const action = lastActions.get(seat.seat);
              // Empty seats never reach here — they returned above.
              if (action === undefined) return null;
              const amount =
                action.kind === 'fold' || action.kind === 'check'
                  ? ''
                  : ` ${bb(action.to, snapshot.bigBlind)}`;
              return (
                <div key={action.id} className={`said ${action.kind}${action.allIn ? ' allin' : ''}`}>
                  {action.allIn ? 'ALL IN' : `${ACTION_WORD[action.kind]}${amount}`}
                </div>
              );
            })()}
            {acting && (
              <>
                {/* Three pixels of twelve-per-cent white under a dark plate is
                    a timer nobody can see, and it was reported as one that was
                    not there. It is a real bar now, and it says how long is
                    left in seconds once the answer starts to matter — which is
                    the number a player actually wants, rather than a length
                    they have to estimate. */}
                <div
                  className={`clock${clock < 0.3 ? ' urgent' : ''}${snapshot.onTimeBank ? ' bank' : ''}`}
                >
                  <div style={{ width: `${clock * 100}%` }} />
                  {secondsLeft !== null && secondsLeft <= 10 && (
                    <span className="clock-count num lat-ui">{secondsLeft}</span>
                  )}
                </div>
                {/* Named, not just coloured. A player watching the game stop
                    needs to know it stopped because somebody is thinking on
                    their own time, not because something broke. */}
                {snapshot.onTimeBank && <span className="tag bank lat-ui">TIME BANK</span>}
              </>
            )}
            {chanceBySeat.has(seat.seat) && (
              <div className="win-chance lat-ui">
                <b className="num">{chanceBySeat.get(seat.seat)!.share.toFixed(1)}%</b>
                {chanceBySeat.get(seat.seat)!.tie > 0.5 && (
                  <span className="split">
                    {chanceBySeat.get(seat.seat)!.tie.toFixed(0)}% split
                  </span>
                )}
              </div>
            )}
            {netBySeat.has(seat.seat) && netBySeat.get(seat.seat) !== 0 && (
              <bdi className={`net ${netBySeat.get(seat.seat)! > 0 ? 'up' : 'down'}`}>
                {netBySeat.get(seat.seat)! > 0 ? '+' : '−'}
                {bb(Math.abs(netBySeat.get(seat.seat)!), snapshot.bigBlind)}
              </bdi>
            )}
          </div>
        );
      })}

      {/* Chips already pushed toward the pot, drawn on the felt itself. */}
      {seats.map((seat) => {
        if (seat.streetCommitted <= 0) return null;
        const anchor = positions[(seat.seat - rotation + snapshot.maxSeats) % snapshot.maxSeats]!;
        return (
          <div
            key={`bet-${seat.seat}`}
            className="bet-chip"
            style={at(anchor) as React.CSSProperties}
          >
{/* A short stack of real chips, in the denomination the bet is worth —
                the way a bet looks across a table before anybody reads the
                number. Never one disc per chip: at a hundred big blinds that
                is unreadable, and the number beside it is the count. */}
            <span className="stack" aria-hidden="true">
              {stackFor(seat.streetCommitted, snapshot.bigBlind).map((kind, index) => (
                <Chip key={index} kind={kind} className="disc" />
              ))}
            </span>
            <span className="num">{bb(seat.streetCommitted, snapshot.bigBlind)}</span>
          </div>
        );
      })}

      {snapshot.buttonSeat !== null &&
        (() => {
          const anchor =
            positions[(snapshot.buttonSeat - rotation + snapshot.maxSeats) % snapshot.maxSeats]!;
          return (
            <div className="dealer-button" title="دکمه" style={at(anchor) as React.CSSProperties}>
              D
            </div>
          );
        })()}
    </div>
  );
}
